import { AnimatedModal } from "@/src/components/AnimatedModal";
import { TextInput } from "@/src/components/tailwind/TextInput";
import {
  AccessQuery,
  DealAccessStatus,
  DealRoleType,
  useCreateDataFileVersionMutation,
  useCreateDealGroupMutation,
  useS3FileUploadCompleteMutation,
} from "@/src/graphql/generated";

import { DealGroupType } from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { classNames } from "@/src/utils/cn";
import { ArrowRightIcon } from "lucide-react";
import { useState, useRef } from "react";
import {
  GranularAccess,
  granularAccessToFileInput,
  granularAccessToFolderInput,
} from ".";
import { EditDataRoomPermissions } from "./EditDataRoomPermissions";
import { Button } from "@/src/components/tailwind/Button";
import { Checkbox } from "@/src/components/checkbox";
import {
  DealRoleOption,
  DealRolesDropdown,
} from "@/src/components/permissions/DealRolesDropdown";
import { EmailPillsInput } from "@/src/components/EmailPillsInput";

export function CreateGroupModal(props: {
  dealId: string;
  type: DealGroupType;
  permissions: AccessQuery["deal"]["dataRoom"]["permissions"];
  onCreate: (id: string) => void;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <AnimatedModal
      open={props.open}
      size="xl"
      onClose={() => {
        props.onClose();
      }}
      padding="0"
    >
      <AnimatedModal.Header title="New group" onClose={props.onClose} />
      <CreateGroupModalContent
        dealId={props.dealId}
        type={props.type}
        onCreate={props.onCreate}
        open={props.open}
        onClose={props.onClose}
        permissions={props.permissions}
      />
    </AnimatedModal>
  );
}

const dealAccessOptions = [
  {
    name: "Manually grant access",
    description:
      "You control when the group gets access. Use when running your own NDA process.",
    value: DealAccessStatus.PendingManualApproval,
  },
  {
    name: "Automatically grant access*",
    description:
      "Upload an NDA and the group gets access immediately after accepting.",
    value: DealAccessStatus.PendingNda,
  },
  {
    name: "No NDA",
    description: "The group can access without signing an NDA.",
    value: DealAccessStatus.Granted,
  },
];

function CreateGroupModalContent(props: {
  dealId: string;
  type: DealGroupType;
  permissions: AccessQuery["deal"]["dataRoom"]["permissions"];
  onCreate: (id: string) => void;
  open: boolean;
  onClose: () => void;
}) {
  const client = useGqlClient();

  const roleType =
    props.type === DealGroupType.Guest
      ? DealRoleType.Guest
      : DealRoleType.Internal;

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [error, setError] = useState("");

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [autoAdmit, setAutoAdmit] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<DealRoleOption | null>(null);

  const createDealGroup = useCreateDealGroupMutation(client);
  const createDataRoomFileVersion = useCreateDataFileVersionMutation(client);

  const fileUploadComplete = useS3FileUploadCompleteMutation(client);

  const [dealAccessOption, setDealAccessOption] = useState(
    dealAccessOptions[0],
  );
  const [dataRoomPermissionIds, setDataRoomPermissionIds] = useState<string[]>(
    [],
  );

  const [overrideDataRoomAccess, setOverrideDataRoomAccess] = useState(false);
  const [granularAccess, setGranularAccess] = useState<GranularAccess>({
    files: {},
    folders: {},
  });

  function clearForm() {
    setName("");
    setFile(null);
    setOverrideDataRoomAccess(false);
    setDealAccessOption(dealAccessOptions[0]);
    setEmails([]);
    setSelectedRole(null);
  }

  return (
    <div className="p-4">
      <div className=" gap-x-2">
        <div className="w-full ">
          <TextInput
            label="Name"
            inputSize="s"
            placeholder="Enter the name of the group..."
            value={name}
            error={nameError}
            onChange={(e) => {
              setNameError("");
              setName(e.currentTarget.value);
            }}
          />
        </div>

        <div className="mt-6 ">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-persian-990 text-sm">
                People{" "}
                <span className="font-semibold ml-1 text-xs text-concrete-400/80 leading-tight">
                  Optional
                </span>
              </p>
              <p className="text-sm text-concrete-600 leading-tight">
                Invite people to the group
              </p>
            </div>
          </div>
          <EmailPillsInput
            className="mt-2"
            emails={emails}
            onChange={setEmails}
          />
          <div className="mt-3">
            <p className="text-sm font-semibold text-persian-990">Role</p>
            <p className="text-sm text-concrete-600 leading-tight">
              This role applies to everyone invited above.
            </p>
            <div className="mt-2">
              <DealRolesDropdown
                dealId={props.dealId}
                roleType={roleType}
                value={selectedRole?.id}
                onChange={setSelectedRole}
                size="s"
              />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <p className="font-semibold text-persian-990 text-sm">
            Data Room Permissions
          </p>
          <p className="text-sm text-concrete-600 leading-tight">
            Select a permission group or specify the specific files you want to
            grant access to
          </p>
          <div className="mt-2 flex gap-x-3">
            {props.permissions.map((permission) => {
              return (
                <button
                  key={permission.id}
                  onClick={() => {
                    setDataRoomPermissionIds((prev) => {
                      if (prev.includes(permission.id)) {
                        return prev.filter((id) => id !== permission.id);
                      } else {
                        return [...prev, permission.id];
                      }
                    });
                  }}
                  className={classNames(
                    "p-2 rounded-md border-2 relative  group",
                    overrideDataRoomAccess ? "border-gray-300 opacity-60" : "",
                    !overrideDataRoomAccess &&
                      dataRoomPermissionIds.includes(permission.id)
                      ? "border-persian-500"
                      : "opacity-50 ",
                  )}
                >
                  <div className="flex items-start gap-x-2 justify-between">
                    <div className="text-left">
                      <p className="text-sm font-semibold text-persian-990">
                        {permission.name}
                      </p>
                      <p className="leading-tight text-xs text-concrete-800">
                        {permission.description}
                      </p>
                      <p className="mt-1 text-xs text-concrete-600">
                        {permission.fileCount} documents
                      </p>
                    </div>
                    <Checkbox
                      onChange={() => {
                        setDataRoomPermissionIds((prev) => {
                          if (prev.includes(permission.id)) {
                            return prev.filter((id) => id !== permission.id);
                          } else {
                            return [...prev, permission.id];
                          }
                        });
                      }}
                      checked={
                        !overrideDataRoomAccess &&
                        dataRoomPermissionIds.includes(permission.id)
                      }
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <p className="mt-6 font-semibold text-persian-990 text-sm">
          Group settings
        </p>
        <div className="mt-2 relative flex items-center">
          <div className="flex h-6 items-center ">
            <input
              id="autoAdmit"
              aria-describedby="autoAdmit-description"
              name="autoAdmit"
              type="checkbox"
              checked={autoAdmit}
              onChange={(e) => {
                setAutoAdmit(e.currentTarget.checked);
              }}
              className="h-4 w-4 rounded border-gray-300 text-persian-600 focus:ring-persian-600"
            />
          </div>
          <div className="ml-3 text-sm leading-6">
            <label
              htmlFor="autoAdmit"
              className="font-medium text-persian-990 leading-tight cursor-pointer"
            >
              Automatically admit guest accounts when they accept the invite
            </label>
          </div>
        </div>

        <EditDataRoomPermissions
          dealGroupId=""
          showPermissions={false}
          initialDataRoomPermission={props.permissions[0]}
          initialGranularAccess={{
            files: [],
            folders: [],
          }}
          dataRoomPermissions={props.permissions}
          overrideDataRoomAccess={overrideDataRoomAccess}
          onOverrideDataRoomAccessChange={(checked) => {
            setOverrideDataRoomAccess(checked);
          }}
          granularAccess={granularAccess}
          onGranularAccessChange={(granularAccess) => {
            setGranularAccess(granularAccess);
          }}
        />

        <div className="mt-8 flex justify-end">
          <Button
            text="Create group"
            icon={ArrowRightIcon}
            variant="positive"
            isLoading={
              createDealGroup.isPending ||
              createDataRoomFileVersion.isPending ||
              uploadingFile
            }
            isDisabled={name === ""}
            onClick={() => {
              if (!name) {
                setNameError("Name is required");
                return;
              }

              if (
                dealAccessOption.value === DealAccessStatus.PendingNda &&
                !file
              ) {
                setFileError("NDA is required");
                return;
              }

              createDealGroup.mutate(
                {
                  input: {
                    dealId: props.dealId,
                    name,
                    type: props.type,
                    overrideDataRoomAccess: overrideDataRoomAccess,
                    filePermissions: granularAccessToFileInput(granularAccess),
                    folderPermissions:
                      granularAccessToFolderInput(granularAccess),
                    dealAccessStatus: DealAccessStatus.Granted,
                    dataRoomPermissionIds: dataRoomPermissionIds,
                    createNdaFile: false,
                    invites:
                      selectedRole == null
                        ? []
                        : emails
                            .filter(
                              (email) =>
                                email !== "" && email.includes("@"),
                            )
                            .map((email) => ({
                              email,
                              dealRoleID: selectedRole.id,
                              dataRoomPermissionID: "",
                              dealGroupID: "",
                              dealID: props.dealId,
                              filePermissions: [],
                              folderPermissions: [],
                              message: "",
                              overrideDataRoomAccess: false,
                              autoAdmit: autoAdmit,
                            })),
                  },
                },
                {
                  onSuccess: (data) => {
                    const createFirmGroupData = data;
                    if (data.createDealGroup.ndaDataRoomFile && file) {
                      createDataRoomFileVersion.mutate(
                        {
                          dataRoomFileID:
                            data.createDealGroup.ndaDataRoomFile.id,
                          fileName: file.name,
                          fileType: file.type,
                          summary: "Initial version",
                        },
                        {
                          onSuccess: (d) => {
                            setUploadingFile(true);
                            fetch(d.createDataRoomFileVersion.url, {
                              method: "PUT",
                              body: file,
                            })
                              .catch((e) => {
                                setUploadingFile(false);
                                setError("Error uploading file");
                              })
                              .then((res) => {
                                fileUploadComplete.mutate(
                                  {
                                    dataRoomFileID:
                                      data.createDealGroup.ndaDataRoomFile!.id,
                                    dataRoomFileVersionID:
                                      d.createDataRoomFileVersion
                                        .dataRoomFileVersionID,
                                  },
                                  {
                                    onSuccess: () => {
                                      setUploadingFile(false);

                                      clearForm();
                                      props.onCreate(
                                        createFirmGroupData.createDealGroup.id,
                                      );
                                    },
                                  },
                                );
                              });
                          },
                        },
                      );
                    }

                    props.onCreate(data.createDealGroup.id);
                    clearForm();
                  },
                  onError: () => {
                    setError("Failed to create group");
                  },
                },
              );

              setNameError("");
            }}
          />
        </div>
      </div>
    </div>
  );
}

